/**
 * Collection Registry — shared per-scope singleton cache for TanStack DB
 * collections.
 *
 * The canonical rule: for a given (table, scope) pair there is exactly ONE
 * collection instance in the browser. Read hooks (`useLiveQuery`) and write
 * paths (`collection.insert/update/delete` → write-through handlers) must
 * resolve the same instance or optimistic state and streamed state diverge.
 *
 * Every entity exposes a `get<Table>Collection(scope…)` accessor below.
 * Hooks should call the accessor rather than holding their own ad-hoc
 * `Map` caches.
 *
 * Collections are intentionally long-lived (the durable Electric shape
 * stream stays open for the session). `disposeCollection` exists for tests
 * and auth-context switches where a stale scope must be dropped.
 */

import {
  createAccountsCollection,
  createCategoriesAccountingCollection,
  createClientActivitiesCollection,
  createClientsCollection,
  createExerciseEntriesCollection,
  createHealthGoalsCollection,
  createHealthMetricEntriesCollection,
  createNotificationsCollection,
  createNutritionEntriesCollection,
  createOpportunitiesCollection,
  createProfilesCollection,
  createTaskCommentsCollection,
  createTaskDependenciesCollection,
  createTimeBlocksCollection,
  createTodosCollection,
} from "./collections";

const registry = new Map<string, unknown>();

/**
 * Return the cached value for `key`, creating it via `create` on first use.
 */
function getOrCreate<T>(key: string, create: () => T): T {
  let value = registry.get(key) as T | undefined;
  if (value === undefined) {
    value = create();
    registry.set(key, value);
  }
  return value;
}

/**
 * Drop a cached collection (tests, scope teardown). Does not stop the
 * underlying shape stream — callers that created a collection with an
 * AbortSignal should abort it separately.
 */
export function disposeCollection(key: string): void {
  registry.delete(key);
}

/** Test hook: drop everything. */
export function disposeAllCollections(): void {
  registry.clear();
}

// ── Scoped accessors ─────────────────────────────────────────────────────────

export function getTodosCollection(userId?: string, orgId?: string) {
  return getOrCreate(`todos:${userId ?? "all"}:${orgId ?? "global"}`, () =>
    createTodosCollection(userId, orgId)
  );
}

export function getProfilesCollection(userId?: string) {
  return getOrCreate(`profiles:${userId ?? "all"}`, () =>
    createProfilesCollection(userId)
  );
}

export function getAccountsCollection(userId?: string, orgId?: string) {
  return getOrCreate(`accounts:${userId ?? "all"}:${orgId ?? "global"}`, () =>
    createAccountsCollection(userId, orgId)
  );
}

export function getCategoriesAccountingCollection(userId?: string, orgId?: string) {
  return getOrCreate(
    `categories_accounting:${userId ?? "all"}:${orgId ?? "global"}`,
    () => createCategoriesAccountingCollection(userId, orgId)
  );
}

export function getNotificationsCollection(userId?: string) {
  return getOrCreate(`notifications:${userId ?? "all"}`, () =>
    createNotificationsCollection(userId)
  );
}

export function getTimeBlocksCollection(userId?: string, orgId?: string) {
  return getOrCreate(`time_blocks:${userId ?? "all"}:${orgId ?? "global"}`, () =>
    createTimeBlocksCollection(userId, orgId)
  );
}

export function getClientsCollection(userId?: string, orgId?: string) {
  return getOrCreate(`clients:${userId ?? "all"}:${orgId ?? "global"}`, () =>
    createClientsCollection(userId, orgId)
  );
}

export function getOpportunitiesCollection(orgId?: string) {
  return getOrCreate(`opportunities:${orgId ?? "global"}`, () =>
    createOpportunitiesCollection(orgId)
  );
}

export function getClientActivitiesCollection(clientId?: string, userId?: string) {
  return getOrCreate(
    `client_activities:${clientId ?? "user"}:${userId ?? "all"}`,
    () => createClientActivitiesCollection(clientId, userId)
  );
}

export function getTaskCommentsCollection(taskId?: string) {
  return getOrCreate(`task_comments:${taskId ?? "global"}`, () =>
    createTaskCommentsCollection(taskId)
  );
}

export function getTaskDependenciesCollection(taskId?: string) {
  return getOrCreate(`task_dependencies:${taskId ?? "global"}`, () =>
    createTaskDependenciesCollection(taskId)
  );
}

export function getNutritionEntriesCollection(userId?: string) {
  return getOrCreate(`nutrition_entries:${userId ?? "all"}`, () =>
    createNutritionEntriesCollection(userId)
  );
}

export function getExerciseEntriesCollection(userId?: string) {
  return getOrCreate(`exercise_entries:${userId ?? "all"}`, () =>
    createExerciseEntriesCollection(userId)
  );
}

export function getHealthMetricEntriesCollection(userId?: string) {
  return getOrCreate(`health_metric_entries:${userId ?? "all"}`, () =>
    createHealthMetricEntriesCollection(userId)
  );
}

export function getHealthGoalsCollection(userId?: string) {
  return getOrCreate(`health_goals:${userId ?? "all"}`, () =>
    createHealthGoalsCollection(userId)
  );
}
